import * as React from 'react';

import { useCollaborationDiagramSync } from 'src/containers/ProjectDiagram/hooks/useCollaborationDiagramSync';
import {
  ContextMenuContext,
  useContextMenuValue,
} from 'src/containers/ProjectDiagram/hooks/useContextMenuValue';
import { useDiagramReadOnly } from 'src/containers/ProjectDiagram/hooks/useDiagramReadOnly';
import {
  SelectionContext,
  useDiagramSelectionValue,
} from 'src/containers/ProjectDiagram/hooks/useDiagramSelectionValue';
import {
  DiagramContext,
  useDiagramValue,
} from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { useHandleDiagramUpdate } from 'src/containers/ProjectDiagram/hooks/useHandleDiagramUpdate';
import {
  TooltipContext,
  useTooltipValue,
} from 'src/containers/ProjectDiagram/hooks/useTooltipValue';
import { useUpdateDiagramData } from 'src/containers/ProjectDiagram/hooks/useUpdateDiagramData';
import { useDiagramSnapshot } from 'src/utils/hooks/useDiagramSnapshot';

type ProjectDiagramContextProviderProps = {
  children: React.ReactNode;
};

export const ProjectDiagramContextProvider: React.VFC<
  ProjectDiagramContextProviderProps
> = ({ children }) => {
  const handleDiagramUpdate = useHandleDiagramUpdate();
  const diagramSnapshot = useDiagramSnapshot();

  const contextMenuValue = useContextMenuValue();
  const selectionValue = useDiagramSelectionValue();
  const tooltipValue = useTooltipValue();

  const [, setContextMenu] = contextMenuValue;
  const [, setSelection] = selectionValue;
  const [, setTooltip] = tooltipValue;

  const handleDiagramSelection = React.useCallback(
    (selection: go.Set<go.Part>) => {
      setSelection(selection.copy());
    },
    [setSelection]
  );

  // Whether to skip building the go.Diagram and edit the model headlessly.
  // Always false today — the diagram is always built. VET-7006 wires this to the
  // model-size threshold / view toggle so large models open in the list view.
  const headless = false;

  // The active edit surface: real diagram or headless facade (see ActiveDiagram).
  const diagram = useDiagramValue({
    diagramSnapshot,
    handleDiagramUpdate,
    handleDiagramSelection,
    headless,
    setContextMenu,
    setTooltip,
  });

  // Canvas-only concerns — no-op headlessly, so they only ever see a real
  // diagram.
  const gojsDiagram = diagram && !diagram.headless ? diagram : undefined;
  useUpdateDiagramData(gojsDiagram);
  useCollaborationDiagramSync(gojsDiagram);
  // Read-only applies to both surfaces (headless commits gate on it too).
  useDiagramReadOnly(diagram);

  return (
    <DiagramContext.Provider value={diagram}>
      <ContextMenuContext.Provider value={contextMenuValue}>
        <SelectionContext.Provider value={selectionValue}>
          <TooltipContext.Provider value={tooltipValue}>
            {children}
          </TooltipContext.Provider>
        </SelectionContext.Provider>
      </ContextMenuContext.Provider>
    </DiagramContext.Provider>
  );
};
